/* Wholesale Purchasing section behavior
   File: assets/wholesale-purchasing.js
   No dependencies beyond an optional Google Maps Places script (only
   loaded if a Google Places API key is configured on the section).
   Works with Shopify's Ajax Cart API and native contact form endpoint. */

(function () {
  "use strict";

  function initAll(scope) {
    (scope || document).querySelectorAll(".wp").forEach(initWholesaleSection);
  }

  initAll(document);

  // Re-initialize when the theme editor live-swaps this section's markup
  // (e.g. after changing a setting like the add-on price), since Shopify
  // does not do a full page reload in that case.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  // ---------- shared Google Maps loader (one script tag total, even with
  // multiple wholesale sections on a page) ----------

  function loadGoogleMaps(apiKey) {
    if (!apiKey) return Promise.resolve(null);
    if (window.google && window.google.maps && window.google.maps.places) {
      return Promise.resolve(window.google);
    }
    if (!window.__wpGoogleMapsPromise) {
      window.__wpGoogleMapsPromise = new Promise((resolve, reject) => {
        const cbName = "__wpGoogleMapsCallback";
        window[cbName] = function () {
          resolve(window.google);
        };
        const script = document.createElement("script");
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=" +
          encodeURIComponent(apiKey) +
          "&libraries=places&callback=" +
          cbName;
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return window.__wpGoogleMapsPromise;
  }

  function setupAddressAutocomplete(inputEl) {
    if (!inputEl || !window.google || !window.google.maps || !window.google.maps.places) return;
    const autocomplete = new google.maps.places.Autocomplete(inputEl, {
      types: ["address"],
    });
    autocomplete.addListener("place_changed", function () {
      const place = autocomplete.getPlace();
      if (place && place.formatted_address) {
        inputEl.value = place.formatted_address;
      }
    });
  }

  function initWholesaleSection(root) {
    const configEl = root.querySelector("[data-wp-config]");
    if (!configEl) return;
    const config = JSON.parse(configEl.textContent);

    const els = {
      cases: Array.from(root.querySelectorAll("[data-wp-case]")),
      qtyInput: root.querySelector("[data-wp-qty-input]"),
      qtyDec: root.querySelector("[data-wp-qty-dec]"),
      qtyInc: root.querySelector("[data-wp-qty-inc]"),
      presetsWrap: root.querySelector("[data-wp-presets]"),
      addonYesBtn: root.querySelector("[data-wp-addon-yes]"),
      addonNoBtn: root.querySelector("[data-wp-addon-no]"),
      addonNote: root.querySelector("[data-wp-addon-note]"),
      quoteAddonYesBtn: root.querySelector("[data-wp-quote-addon-yes]"),
      quoteAddonNoBtn: root.querySelector("[data-wp-quote-addon-no]"),
      unitPrice: root.querySelector("[data-wp-unit-price]"),
      priceQty: root.querySelector("[data-wp-price-qty]"),
      savingsBadge: root.querySelector("[data-wp-savings]"),
      tierRange: root.querySelector("[data-wp-tier-range]"),
      tierFill: root.querySelector("[data-wp-tier-fill]"),
      tierNote: root.querySelector("[data-wp-tier-note]"),
      total: root.querySelector("[data-wp-total]"),
      buyNowBtn: root.querySelector("[data-wp-buy-now]"),
      addToCartBtn: root.querySelector("[data-wp-add-to-cart]"),
      quoteBtn: root.querySelector("[data-wp-quote]"),
      status: root.querySelector("[data-wp-status]"),
      dialog: root.querySelector("[data-wp-quote-dialog]"),
      dialogCancelBtns: Array.from(root.querySelectorAll("[data-wp-quote-cancel]")),
      dialogSubmit: root.querySelector("[data-wp-quote-submit]"),
      dialogStatus: root.querySelector("[data-wp-dialog-status]"),
      confirmDialog: root.querySelector("[data-wp-confirm-dialog]"),
      confirmCloseBtns: Array.from(root.querySelectorAll("[data-wp-confirm-close]")),
      quoteOrg: root.querySelector("[data-wp-quote-org]"),
      quoteFirstName: root.querySelector("[data-wp-quote-first-name]"),
      quoteLastName: root.querySelector("[data-wp-quote-last-name]"),
      quoteEmail: root.querySelector("[data-wp-quote-email]"),
      quoteEmailConfirm: root.querySelector("[data-wp-quote-email-confirm]"),
      quotePhone: root.querySelector("[data-wp-quote-phone]"),
      quoteShipping: root.querySelector("[data-wp-quote-shipping]"),
      quoteBillingSame: root.querySelector("[data-wp-quote-billing-same]"),
      quoteBillingWrap: root.querySelector("[data-wp-billing-wrap]"),
      quoteBilling: root.querySelector("[data-wp-quote-billing]"),
      quoteTaxExempt: root.querySelector("[data-wp-quote-tax-exempt]"),
      quoteComments: root.querySelector("[data-wp-quote-comments]"),
      quoteForm: root.querySelector("[data-wp-quote-dialog] form"),
    };

    if (!els.cases.length || !els.qtyInput) return;

    const addonVariantId = root.dataset.addonVariantId
      ? parseInt(root.dataset.addonVariantId, 10)
      : null;
    const addonUnitPrice = 0.75; // fixed $0.75/unit, ignores any section setting
    const addonMinQty = config.addonMinQty || 500;
    const addonNoteDefaultText = els.addonNote ? els.addonNote.textContent : "";
    const minQty = parseInt(els.qtyInput.min || "1", 10) || 1;

    let selectedVariant = findVariant(getSelectedCaseEl().dataset.variantId);
    let customizationOn = false; // state behind the "Customize your order?" Yes/No toggle

    // ---------- Google Places autocomplete (optional) ----------

    if (config.googlePlacesApiKey) {
      loadGoogleMaps(config.googlePlacesApiKey)
        .then(function (google) {
          if (!google) return;
          setupAddressAutocomplete(els.quoteShipping);
          setupAddressAutocomplete(els.quoteBilling);
        })
        .catch(function () {
          // Maps failed to load (bad key, network, etc). Fields silently
          // fall back to plain text entry — no error surfaced to the shopper.
        });
    }

    // ---------- helpers ----------

    function getSelectedCaseEl() {
      return els.cases.find((c) => c.classList.contains("is-selected")) || els.cases[0];
    }

    function findVariant(id) {
      const numId = parseInt(id, 10);
      return config.variants.find((v) => v.id === numId) || config.variants[0];
    }

    function formatMoney(cents) {
      // cents is a plain dollar amount here (not Shopify's integer-cents format)
      const amount = (Math.round(cents * 100) / 100).toFixed(2);
      const format = config.moneyFormat || "${{amount}}";
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    function currentQty() {
      let q = parseInt(els.qtyInput.value, 10);
      if (isNaN(q) || q < minQty) q = minQty;
      return q;
    }

    // Resolve a per-unit price for the given quantity.
    // Prefers the variant's real native quantity_price_breaks (B2B / company
    // price lists) since those are what checkout will actually charge.
    // Falls back to the section's configured tier list for display.
    function resolvePricing(qty) {
      const breaks = (selectedVariant && selectedVariant.quantityPriceBreaks) || [];
      if (breaks.length) {
        const sorted = [...breaks].sort((a, b) => a.minQty - b.minQty);
        let applicable = sorted.filter((b) => qty >= b.minQty);
        let tier = applicable.length ? applicable[applicable.length - 1] : sorted[0];
        const next = sorted.find((b) => b.minQty > tier.minQty);
        return {
          price: tier.price,
          source: "native",
          rangeLabel: next ? `${tier.minQty}\u2013${next.minQty - 1} units` : `${tier.minQty}+ units`,
          isTopTier: !next,
          progress: next
            ? Math.min(1, (qty - tier.minQty) / (next.minQty - tier.minQty))
            : 1,
          retailPrice: null,
        };
      }

      const tiers = (config.tiers || []).slice().sort((a, b) => a.minQty - b.minQty);
      if (!tiers.length) {
        return {
          price: selectedVariant ? selectedVariant.price : 0,
          source: "variant",
          rangeLabel: "",
          isTopTier: true,
          progress: 1,
          retailPrice: null,
        };
      }
      let applicable = tiers.filter((t) => qty >= t.minQty);
      let tier = applicable.length ? applicable[applicable.length - 1] : tiers[0];
      const next = tiers.find((t) => t.minQty > tier.minQty);
      return {
        price: tier.price,
        source: "fallback",
        rangeLabel: next ? `${tier.minQty}\u2013${next.minQty - 1} units` : `${tier.minQty}+ units`,
        isTopTier: !next,
        progress: next ? Math.min(1, (qty - tier.minQty) / (next.minQty - tier.minQty)) : 1,
        retailPrice: tier.retailPrice || null,
      };
    }

    // Keep both Yes/No toggles (the main config card and the quote dialog)
    // in sync with each other and with the underlying state.
    function setAddonSelection(on) {
      customizationOn = on;
      if (els.addonYesBtn) els.addonYesBtn.classList.toggle("is-selected", on);
      if (els.addonNoBtn) els.addonNoBtn.classList.toggle("is-selected", !on);
      if (els.quoteAddonYesBtn) els.quoteAddonYesBtn.classList.toggle("is-selected", on);
      if (els.quoteAddonNoBtn) els.quoteAddonNoBtn.classList.toggle("is-selected", !on);
    }

    // Enable/disable the "Yes" option based on quantity, and keep the
    // helper text in sync.
    function updateAddonAvailability(qty) {
      if (!els.addonYesBtn || !els.addonNoBtn) return;
      const eligible = qty >= addonMinQty;
      els.addonYesBtn.disabled = !eligible;
      if (els.quoteAddonYesBtn) els.quoteAddonYesBtn.disabled = !eligible;
      if (!eligible && customizationOn) {
        setAddonSelection(false);
      } else {
        setAddonSelection(customizationOn);
      }
      if (els.addonNote) {
        els.addonNote.textContent = eligible
          ? `${addonNoteDefaultText} \u2014 +${formatMoney(addonUnitPrice)}/unit`
          : `Customization only available for orders of ${addonMinQty.toLocaleString()} units or more.`;
      }
    }

    function render() {
      const qty = currentQty();
      updateAddonAvailability(qty);
      const pricing = resolvePricing(qty);
      const addonOn = customizationOn;
      const unit = pricing.price + (addonOn ? addonUnitPrice : 0);
      const total = unit * qty;

      els.unitPrice.textContent = formatMoney(unit);
      els.priceQty.textContent = qty.toLocaleString();
      els.total.textContent = formatMoney(total);

      if (pricing.retailPrice && pricing.retailPrice > pricing.price) {
        const pct = Math.round((1 - pricing.price / pricing.retailPrice) * 100);
        els.savingsBadge.textContent = `Save ~${pct}% vs. retail`;
        els.savingsBadge.hidden = false;
      } else {
        els.savingsBadge.hidden = true;
      }

      els.tierRange.textContent = pricing.rangeLabel || "\u2014";
      els.tierFill.style.width = `${Math.round(pricing.progress * 100)}%`;
      els.tierNote.textContent = pricing.isTopTier
        ? "You're at the top wholesale tier."
        : "Increase quantity to unlock the next tier.";

      // keep preset button highlight in sync with manual typing
      if (els.presetsWrap) {
        Array.from(els.presetsWrap.children).forEach((btn) => {
          btn.classList.toggle("is-selected", parseInt(btn.dataset.wpPreset, 10) === qty);
        });
      }

      const inStock = !selectedVariant || selectedVariant.available !== false;
      if (els.buyNowBtn) {
        els.buyNowBtn.disabled = !inStock;
        els.buyNowBtn.textContent = inStock ? "Buy now" : "Currently unavailable";
      }
      if (els.addToCartBtn) {
        els.addToCartBtn.disabled = !inStock;
        els.addToCartBtn.textContent = inStock ? "Add to cart" : "Currently unavailable";
      }
    }

    function setStatus(message, state) {
      if (!els.status) return;
      els.status.textContent = message;
      els.status.hidden = !message;
      if (state) els.status.setAttribute("data-state", state);
      else els.status.removeAttribute("data-state");
    }

    function setDialogStatus(message, state) {
      if (!els.dialogStatus) return;
      els.dialogStatus.textContent = message;
      els.dialogStatus.hidden = !message;
      if (state) els.dialogStatus.setAttribute("data-state", state);
      else els.dialogStatus.removeAttribute("data-state");
    }

    // Builds the /cart/add.js line items for the current selection. Shared
    // by both "Add to cart" and "Buy now" since they need identical lines,
    // just a different action afterward (stay on page vs. go to checkout).
    function buildCartItems(qty) {
      const items = [{ id: selectedVariant.id, quantity: qty }];
      const addonOn = customizationOn;

      if (addonOn && addonVariantId) {
        // A real add-on product is linked: add it as its own cart line so
        // it's actually charged.
        items[0].properties = { "Custom logo & packaging insert": "Yes (see add-on line)" };
        items.push({
          id: addonVariantId,
          quantity: qty,
          properties: { "Attached to": `${selectedVariant.productTitle} \u00d7 ${qty}` },
        });
      } else if (addonOn) {
        // No add-on product linked: note it on the order so staff can
        // follow up and invoice/quote the difference manually.
        items[0].properties = {
          "Custom logo & packaging insert": `Yes \u2014 +${formatMoney(addonUnitPrice)}/unit (not charged automatically, contact for invoicing)`,
        };
      }
      return items;
    }

    // Re-syncs the visible cart count / icon after an Ajax add, without a
    // page reload. Covers the most common theme patterns; if your theme's
    // cart badge still doesn't update, it likely uses a custom element or
    // section id this doesn't recognize — tell me your theme name and I'll
    // target it specifically.
    async function refreshCartUI() {
      let cart = null;
      try {
        const cartRes = await fetch("/cart.js");
        cart = await cartRes.json();
      } catch (e) {
        return; // Can't refresh what we can't fetch.
      }

      // Horizon (Shopify's current default theme, built on web components)
      // updates its header cart icon and <cart-drawer-component> by
      // listening for this specific "cart:update" event on document, with
      // the new item count in detail.data.itemCount. This isn't in public
      // docs; it's the event Horizon's own product-form component dispatches
      // internally, confirmed working on Horizon 3.x.
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: "wp-add-to-cart",
            },
          },
        })
      );

      // Also open the drawer so the add is visually obvious rather than
      // relying on the inline status message alone. Delete this block if
      // you'd rather the drawer stayed closed and only the badge updated.
      const cartDrawer =
        document.querySelector("cart-drawer-component") || document.querySelector("cart-drawer");
      if (cartDrawer) {
        try {
          cartDrawer.open = true;
        } catch (e) {
          // Older/newer Horizon builds may expose a different API; ignore.
        }
      }

      // Fallback: directly update any plain-text cart badges in case the
      // theme (or an app) renders one outside Horizon's component system.
      const countSelectors = ["[data-cart-count]", ".cart-count-bubble", ".cart-count", "#CartCount"];
      countSelectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.textContent = cart.item_count;
          if ("hidden" in el) el.hidden = cart.item_count === 0;
        });
      });
    }

    // ---------- events: case selection ----------

    els.cases.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.dataset.variantAvailable === "false") {
          setStatus("That case is currently out of stock.", "error");
          return;
        }
        els.cases.forEach((c) => {
          c.classList.remove("is-selected");
          c.setAttribute("aria-checked", "false");
        });
        btn.classList.add("is-selected");
        btn.setAttribute("aria-checked", "true");
        selectedVariant = findVariant(btn.dataset.variantId);
        setStatus("", null);
        render();
      });
    });

    // ---------- events: quantity ----------

    function setQty(q) {
      q = Math.max(minQty, Math.round(q));
      els.qtyInput.value = q;
      render();
    }

    els.qtyInput.addEventListener("input", render);
    els.qtyInput.addEventListener("change", () => setQty(currentQty()));
    if (els.qtyDec) els.qtyDec.addEventListener("click", () => setQty(currentQty() - 50));
    if (els.qtyInc) els.qtyInc.addEventListener("click", () => setQty(currentQty() + 50));

    if (els.presetsWrap) {
      els.presetsWrap.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-wp-preset]");
        if (!btn) return;
        setQty(parseInt(btn.dataset.wpPreset, 10));
      });
    }

    // ---------- events: "Customize your order?" Yes/No toggle ----------

    if (els.addonYesBtn) {
      els.addonYesBtn.addEventListener("click", () => {
        if (els.addonYesBtn.disabled) return;
        setAddonSelection(true);
        render();
      });
    }
    if (els.addonNoBtn) {
      els.addonNoBtn.addEventListener("click", () => {
        setAddonSelection(false);
        render();
      });
    }
    if (els.quoteAddonYesBtn) {
      els.quoteAddonYesBtn.addEventListener("click", () => {
        if (els.quoteAddonYesBtn.disabled) return;
        setAddonSelection(true);
        render();
      });
    }
    if (els.quoteAddonNoBtn) {
      els.quoteAddonNoBtn.addEventListener("click", () => {
        setAddonSelection(false);
        render();
      });
    }

    // ---------- buy now: real Ajax Cart add + redirect to checkout ----------

    if (els.buyNowBtn) {
      els.buyNowBtn.addEventListener("click", async () => {
        const qty = currentQty();
        if (!selectedVariant) return;

        els.buyNowBtn.disabled = true;
        els.buyNowBtn.textContent = "Adding\u2026";
        setStatus("", null);

        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: buildCartItems(qty) }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || "Could not add this item to your cart.");
          }
          window.location.href = "/checkout";
        } catch (err) {
          setStatus(err.message || "Something went wrong adding this to your cart.", "error");
          els.buyNowBtn.disabled = false;
          els.buyNowBtn.textContent = "Buy now";
        }
      });
    }

    // ---------- add to cart: same line items, but stay on the page so
    // people can add another case/color before checking out ----------

    if (els.addToCartBtn) {
      els.addToCartBtn.addEventListener("click", async () => {
        const qty = currentQty();
        if (!selectedVariant) return;

        els.addToCartBtn.disabled = true;
        els.addToCartBtn.textContent = "Adding\u2026";
        setStatus("", null);

        const caseLabel = getSelectedCaseEl().querySelector(".wp-case-name")?.textContent || "item";

        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: buildCartItems(qty) }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || "Could not add this item to your cart.");
          }
          setStatus(`Added ${qty.toLocaleString()} units \u2014 ${caseLabel} \u2014 to your cart.`, "ok");
          await refreshCartUI();
        } catch (err) {
          setStatus(err.message || "Something went wrong adding this to your cart.", "error");
        } finally {
          els.addToCartBtn.disabled = false;
          els.addToCartBtn.textContent = "Add to cart";
        }
      });
    }

    // ---------- request a formal quote: wholesale account dialog ----------

    if (els.quoteBillingSame && els.quoteBillingWrap) {
      const syncBillingVisibility = () => {
        els.quoteBillingWrap.hidden = els.quoteBillingSame.checked;
        if (els.quoteBillingSame.checked && els.quoteBilling) {
          els.quoteBilling.value = "";
        }
      };
      // Browsers restore checkbox state across reloads, so set the initial
      // visibility from the checkbox rather than assuming it starts unchecked.
      syncBillingVisibility();
      els.quoteBillingSame.addEventListener("change", syncBillingVisibility);
    }

    if (els.confirmCloseBtns.length && els.confirmDialog) {
      els.confirmCloseBtns.forEach((btn) => {
        btn.addEventListener("click", () => els.confirmDialog.close());
      });
    }

    if (els.quoteBtn && els.dialog) {
      els.quoteBtn.addEventListener("click", () => {
        setDialogStatus("", null);
        els.dialog.showModal();
      });

      els.dialogCancelBtns.forEach((btn) => {
        btn.addEventListener("click", () => els.dialog.close());
      });

      // Shopify's spam protection rejects a POST to /contact that carries no
      // hCaptcha token. The widget is injected into this form by Shopify at page
      // load; execute it and read the token back before submitting. Resolves to
      // null when spam protection is off (no widget on the page) so the request
      // still goes out.
      function getCaptchaToken() {
        const widget = els.quoteForm && els.quoteForm.querySelector(".h-captcha");
        const iframe = widget && widget.querySelector("iframe");
        const widgetId =
          iframe && (iframe.dataset.hcaptchaWidgetId || iframe.getAttribute("data-hcaptcha-widget-id"));

        if (!window.hcaptcha || !widgetId) return Promise.resolve(null);

        const existing = iframe.dataset.hcaptchaResponse || iframe.getAttribute("data-hcaptcha-response");
        if (existing) return Promise.resolve(existing);

        window.hcaptcha.execute(widgetId);

        return new Promise((resolve, reject) => {
          const start = Date.now();
          const poll = setInterval(() => {
            const token = iframe.dataset.hcaptchaResponse || iframe.getAttribute("data-hcaptcha-response");
            if (token) {
              clearInterval(poll);
              resolve(token);
            } else if (Date.now() - start > 10000) {
              clearInterval(poll);
              reject(new Error("Captcha verification timed out. Please try again."));
            }
          }, 100);
        });
      }

      // Enter inside a text field would otherwise navigate away from the page,
      // since this is a real Shopify contact form now.
      if (els.quoteForm) {
        els.quoteForm.addEventListener("submit", (event) => {
          event.preventDefault();
          els.dialogSubmit.click();
        });
      }

      els.dialogSubmit.addEventListener("click", async () => {
        if (els.dialogSubmit.disabled) return;
        setDialogStatus("", null);

        // ----- validation -----
        const requiredFields = [els.quoteOrg, els.quoteFirstName, els.quoteLastName, els.quoteEmail, els.quoteEmailConfirm, els.quoteShipping];
        for (const field of requiredFields) {
          if (field && !field.reportValidity()) return;
        }

        if (els.quoteEmail.value.trim().toLowerCase() !== els.quoteEmailConfirm.value.trim().toLowerCase()) {
          els.quoteEmailConfirm.setCustomValidity("Work email and confirmation don't match.");
          els.quoteEmailConfirm.reportValidity();
          els.quoteEmailConfirm.addEventListener(
            "input",
            () => els.quoteEmailConfirm.setCustomValidity(""),
            { once: true }
          );
          return;
        }

        const billingSame = !!(els.quoteBillingSame && els.quoteBillingSame.checked);
        if (!billingSame && els.quoteBilling && !els.quoteBilling.value.trim()) {
          els.quoteBilling.setCustomValidity("Enter a billing address, or check \u201csame as shipping.\u201d");
          els.quoteBilling.reportValidity();
          els.quoteBilling.addEventListener(
            "input",
            () => els.quoteBilling.setCustomValidity(""),
            { once: true }
          );
          return;
        }

        els.dialogSubmit.disabled = true;
        els.dialogSubmit.textContent = "Sending\u2026";

        // Recompute selection/pricing at submit time (rather than caching it
        // when the dialog opened) so it always reflects what's currently
        // selected on the page.
        const qty = currentQty();
        const pricing = resolvePricing(qty);
        const addonOn = customizationOn;
        const unit = pricing.price + (addonOn ? addonUnitPrice : 0);
        const total = unit * qty;
        const caseLabel = getSelectedCaseEl().querySelector(".wp-case-name")?.textContent || "";
        const productTitle = selectedVariant ? selectedVariant.productTitle : "";

        const billingAddress = billingSame ? els.quoteShipping.value : els.quoteBilling.value;
        const taxExempt = !!(els.quoteTaxExempt && els.quoteTaxExempt.checked);
        const firstName = els.quoteFirstName.value.trim();
        const lastName = els.quoteLastName.value.trim();
        const fullName = `${firstName} ${lastName}`.trim();

        // Each piece of the request is sent as its own contact[FieldName]
        // field (rather than one big contact[body] blob) so Shopify's
        // default contact-notification email renders it as its own bolded
        // field with the value underneath, matching the reference layout.
        const body = new URLSearchParams();
        body.set("form_type", "contact");
        body.set("utf8", "\u2713");
        // Static identifier so staff/apps parsing contact notifications can
        // tell this form apart from the theme's other contact forms.
        body.set("contact[Form Source]", "bulk-order-inquiry");
        // Sent next so it lands right under "Email" in the notification,
        // matching the reference layout (visitor's detected country, from
        // Shopify's localization object — see the Liquid template).
        body.set("contact[Country Code]", config.countryCode || "\u2014");
        body.set("contact[name]", fullName);
        body.set("contact[email]", els.quoteEmail.value);
        body.set("contact[body]", config.quoteSubjectPrefix);
        body.set("contact[Organization]", els.quoteOrg.value);
        body.set("contact[First name]", firstName);
        body.set("contact[Last name]", lastName);
        body.set("contact[Contact]", fullName);
        body.set("contact[Phone]", els.quotePhone.value || "\u2014");
        body.set("contact[Shipping address]", els.quoteShipping.value);
        body.set(
          "contact[Billing address]",
          `${billingAddress}${billingSame ? " (same as shipping)" : ""}`
        );
        body.set(
          "contact[Tax-exempt]",
          taxExempt ? "Yes \u2014 will provide certificate for review" : "No"
        );
        body.set(
          "contact[Comments/questions]",
          els.quoteComments && els.quoteComments.value.trim() ? els.quoteComments.value.trim() : "\u2014"
        );
        body.set("contact[Selection]", `${productTitle} \u2014 ${caseLabel}.`);
        body.set("contact[Quantity]", `${qty.toLocaleString()} units.`);
        body.set("contact[Customization]", addonOn ? "Yes" : "No");
        body.set("contact[Estimated total]", `${formatMoney(total)}.`);
        body.set(
          "contact[Product link]",
          `${window.location.origin}${selectedVariant ? selectedVariant.productUrl : ""}`
        );

        try {
          const captchaToken = await getCaptchaToken();
          if (captchaToken) body.set("h-captcha-response", captchaToken);

          const res = await fetch("/contact", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          if (!res.ok) {
            // eslint-disable-next-line no-console
            console.error("Wholesale quote /contact request failed:", res.status, res.statusText, await res.text().catch(() => ""));
            throw new Error(`Request failed (${res.status})`);
          }

          els.dialog.close();
          const formEl = els.dialog.querySelector("form");
          if (formEl) formEl.reset();
          setDialogStatus("", null);

          if (els.confirmDialog) {
            els.confirmDialog.showModal();
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Wholesale quote submission error:", err);
          setDialogStatus(
            err && /captcha/i.test(err.message || "")
              ? err.message
              : "Could not send your quote request. Please try again.",
            "error"
          );
        } finally {
          els.dialogSubmit.disabled = false;
          els.dialogSubmit.textContent = "Submit for quote";
        }
      });
    }

    render();
  }
})();
